import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "sonner";
import { lazy, Suspense } from "react";
import Landing from "@/pages/Landing";
import SiteLegalBar from "@/components/SiteLegalBar";
import CookieBanner from "@/components/CookieBanner";
import AdminRoute from "@/components/AdminRoute";

// Carregamento sob pedido: a página inicial só descarrega o seu próprio
// código; o editor (motor de fios, PDF, G-code), o painel admin e as
// restantes páginas só são descarregados quando o visitante lá chega.
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Editor = lazy(() => import("@/pages/Editor"));
const OrderPage = lazy(() => import("@/pages/OrderPage"));
const OrderSuccess = lazy(() => import("@/pages/OrderSuccess"));
const Store = lazy(() => import("@/pages/Store"));
const Cart = lazy(() => import("@/pages/Cart"));
const CreateOrder = lazy(() => import("@/pages/CreateOrder"));
const MyOrders = lazy(() => import("@/pages/MyOrders"));
const MyOrderDetail = lazy(() => import("@/pages/MyOrderDetail"));
const TrackOrder = lazy(() => import("@/pages/TrackOrder"));
const Sobre = lazy(() => import("@/pages/Sobre"));
const Exemplos = lazy(() => import("@/pages/Exemplos"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Legal = lazy(() => import("@/pages/Legal"));
const SharedArt = lazy(() => import("@/pages/SharedArt"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminProduction = lazy(() => import("@/pages/admin/AdminProduction"));
const AdminProjects = lazy(() => import("@/pages/admin/AdminProjects"));
const AdminMachine = lazy(() => import("@/pages/admin/AdminMachine"));
const AdminTools = lazy(() => import("@/pages/admin/AdminTools"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-[#09090B]" data-testid="page-loader">
      <div className="w-8 h-8 border-2 border-[#222225] border-t-[#FF3B00] rounded-full animate-spin" />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
        <CartProvider>
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "#1e1e22",
                border: "1px solid #3f3f46",
                color: "#fff",
                borderRadius: "2px",
              },
            }}
          />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Public sales funnel: customize -> order -> pay */}
            <Route path="/personalizar" element={<Editor />} />
            <Route path="/encomendar" element={<OrderPage />} />
            <Route path="/encomenda/sucesso" element={<OrderSuccess />} />
            {/* Loja online + fluxo simplificado do cliente */}
            <Route path="/loja" element={<Store />} />
            <Route path="/carrinho" element={<Cart />} />
            <Route path="/criar" element={<CreateOrder />} />
            <Route path="/consultar-encomenda" element={<TrackOrder />} />
            <Route path="/sobre" element={<Sobre />} />
            <Route path="/exemplos" element={<Exemplos />} />
            <Route
              path="/minhas-encomendas"
              element={
                <ProtectedRoute>
                  <MyOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/minhas-encomendas/:id"
              element={
                <ProtectedRoute>
                  <MyOrderDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editor"
              element={
                <ProtectedRoute>
                  <Editor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/editor/:id"
              element={
                <ProtectedRoute>
                  <Editor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminLayout />
                </AdminRoute>
              }
            >
              <Route index element={<AdminOverview />} />
              <Route path="encomendas" element={<AdminOrders />} />
              <Route path="projetos" element={<AdminProjects />} />
              <Route path="maquina" element={<AdminMachine />} />
              <Route path="producao" element={<AdminProduction />} />
              <Route path="ferramentas" element={<AdminTools />} />
              <Route path="loja" element={<AdminProducts />} />
              <Route path="utilizadores" element={<AdminUsers />} />
              <Route path="configuracoes" element={<AdminSettings />} />
            </Route>
            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/p/:token" element={<SharedArt />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          <SiteLegalBar />
          <CookieBanner />
        </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
